import React, { useState } from "react";

import { IconButton, Tooltip } from "@mui/material";

import { colors } from "@zapalacorp/tokens";
import { ChevronDown } from "Components/Generic/Icons/ChevronDown";
import { ChevronUp } from "Components/Generic/Icons/ChevronUp";
import { IconAirFreight } from "Components/Generic/Icons/IconAirFreight";
import { IconMaterial } from "Components/Generic/Icons/IconMaterial";
import { IconNotGoing } from "Components/Generic/Icons/IconNotGoing";
import { IconRoadFreight } from "Components/Generic/Icons/IconRoadFreight";
import { IconSeaFreight } from "Components/Generic/Icons/IconSeaFreight";
import { Destination } from "Models/destination";
import { useTranslation } from "react-i18next";
import { FiLink2 } from "react-icons/fi";

import {
  DestinationResidenceCustomerPortal,
  ItemJobCustomerPortal,
  MaterialCustomerPortal,
  RoomJobCustomerPortal,
} from "../../../../../../../models/customer.portal";
import { Metric, handleGetConvertValue } from "../../../../../../../util/converts";
import { DESTINATION, MODE, Mode } from "../../../../../../../util/enums";
import { getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import {
  ItemDescription,
  ItemInfo,
  ItemInfoText,
  ItemInfoWrapper,
  ItemsAndMaterialsContainer,
  ItemsWrapper,
  RoomCardsContainer,
  RoomContentCard,
  RoomContentCardTitle,
  RoomInfoHeader,
  RoomInfoText,
  RoomItemsInfo,
  RoomName,
} from "./styles";

interface EstimatedRoomContentProps {
  room: RoomJobCustomerPortal;
  items?: ItemJobCustomerPortal[];
  materials?: MaterialCustomerPortal[];
  roomName?: string;
}

export const EstimatedRoomMobile = ({
  items,
  materials,
  roomName,
  room,
}: EstimatedRoomContentProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { t: translate } = useTranslation();

  const measurementWeight = getWeightMeasurement();

  const sumItensAndMaterials = (): number => {
    let materialSum = 0;
    const itemsSum = room.itemsJob
      ?.filter(
        (item) => item?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
      )
      ?.reduce((total: number, item: ItemJobCustomerPortal) => total + Number(item?.quantity), 0);

    room?.materials?.forEach((material) => {
      if (
        material?.serviceBooking?.isItem &&
        material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
      ) {
        materialSum += material.quantity;
      }
    });

    return Number(itemsSum) + Number(materialSum);
  };

  const totalWeight = () => {
    let itemsTotal = 0;
    let materialsTotal = 0;

    if (room.itemsJob.length > 0) {
      itemsTotal = room.itemsJob
        ?.filter(
          (item) => item?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, item: ItemJobCustomerPortal) =>
            total +
            Number(
              Number(
                handleGetConvertValue(
                  Number(item?.weight),
                  measurementWeight?.code || Metric.kg,
                  measurementWeight?.decimalPlaces
                )
              ) * Number(item?.quantity)
            ),
          0
        );
    }

    if (room?.materials?.length > 0) {
      materialsTotal = room?.materials
        ?.filter(
          (material) =>
            material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, material: MaterialCustomerPortal) =>
            total +
            (material?.serviceBooking?.isItem
              ? Number(
                  handleGetConvertValue(
                    Number(material?.weight || 0),
                    measurementWeight?.code || Metric.kg,
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(material.quantity)
              : 0),
          0
        );
    }

    return Number(itemsTotal + materialsTotal).toFixed(2);
  };

  const totalQuantity = sumItensAndMaterials();

  const getIconByModeOrDestination = (
    mode?: MODE | string,
    destination?: Destination,
    destinationResidence?: DestinationResidenceCustomerPortal
  ) => {
    if (destination) {
      const destinationName = destination?.name;

      switch (destinationName?.toLowerCase()) {
        case DESTINATION.NOT_GOING.toLowerCase():
          return <IconNotGoing width={20} height={20} color={colors.gray800} />;
        case DESTINATION.AIR.toLowerCase():
          return <IconAirFreight width={20} height={20} color={colors.gray800} />;
        case DESTINATION.SEA.toLowerCase():
          return <IconSeaFreight width={20} height={20} color={colors.gray800} />;
        case DESTINATION.ROAD.toLocaleLowerCase():
          return <IconRoadFreight width={20} height={20} color={colors.gray800} />;
        default:
          return;
      }
    }
    if (mode) {
      switch (mode?.toLowerCase()) {
        case Mode.Road.toLowerCase():
          return <IconRoadFreight width={20} height={20} color={colors.gray800} />;
        case Mode.Air.toLowerCase():
          return <IconAirFreight width={20} height={20} color={colors.gray800} />;
        case Mode.Sea.toLowerCase():
          return <IconSeaFreight width={20} height={20} color={colors.gray800} />;
        default:
          return null;
      }
    }

    if (destinationResidence) {
      return <IconRoadFreight width={20} height={20} color={colors.gray800} />;
    }

    return null;
  };

  const renderItems = (items?: ItemJobCustomerPortal[]) => {
    if (items && items?.length) {
      return (
        <ItemsWrapper>
          <ItemInfoText>{translate("items")}</ItemInfoText>
          <RoomCardsContainer>
            {items?.map((item: ItemJobCustomerPortal, index: number) => {
              return (
                <RoomContentCard key={index}>
                  <RoomContentCardTitle color={colors.gray800}>
                    {getIconByModeOrDestination(
                      item?.mode?.toLowerCase(),
                      item?.destination,
                      item?.destinationResidence
                    )}
                    <ItemDescription>{item?.description || ""}</ItemDescription>
                  </RoomContentCardTitle>

                  <ItemInfoWrapper>
                    <div>
                      <ItemInfoText>{translate("quantity")}</ItemInfoText>
                      <ItemInfo>{item?.quantity || 1}</ItemInfo>
                    </div>
                    <div>
                      <ItemInfoText>{translate("weight")}</ItemInfoText>
                      <ItemInfo>
                        {Number(item?.weight || 0).toFixed(2)}
                        {measurementWeight?.code || "kg"}
                      </ItemInfo>
                    </div>
                  </ItemInfoWrapper>
                </RoomContentCard>
              );
            })}
          </RoomCardsContainer>
        </ItemsWrapper>
      );
    }

    return null;
  };

  const renderMaterials = (materials?: MaterialCustomerPortal[]) => {
    if (materials && materials?.length) {
      return (
        <div>
          <ItemInfoText>{translate("materials")}</ItemInfoText>
          <RoomCardsContainer>
            {materials?.map((material: MaterialCustomerPortal, index: number) => {
              return (
                <RoomContentCard key={index}>
                  <RoomContentCardTitle color={colors.gray800}>
                    {<IconMaterial width={20} height={20} color={colors.gray800} />}
                    <ItemDescription>{material?.serviceBooking?.name || ""}</ItemDescription>
                    {material?.itemJobId && (
                      <Tooltip
                        placement="top"
                        title={`${translate("linkedTo")} ${material?.itemJob?.description}`}
                      >
                        <div>
                          <FiLink2 color={colors.brandBlue500} />
                        </div>
                      </Tooltip>
                    )}
                  </RoomContentCardTitle>

                  <ItemInfoWrapper>
                    <div>
                      <ItemInfoText>{translate("quantity")}</ItemInfoText>
                      <ItemInfo>{material?.quantity || 1}</ItemInfo>
                    </div>
                    <div>
                      <ItemInfoText>{translate("weight")}</ItemInfoText>
                      <ItemInfo>
                        {Number(material?.weight || 0).toFixed(2)}
                        {measurementWeight?.code || "kg"}
                      </ItemInfo>
                    </div>
                  </ItemInfoWrapper>
                </RoomContentCard>
              );
            })}
          </RoomCardsContainer>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <RoomInfoHeader>
        <div>
          <RoomName>{roomName || ""}</RoomName>
          <RoomItemsInfo>
            <li>
              <RoomInfoText>
                {totalQuantity} {totalQuantity > 1 ? translate("items") : translate("item")}
              </RoomInfoText>
            </li>
            <li>
              <RoomInfoText>
                {totalWeight()} {measurementWeight?.code}
              </RoomInfoText>
            </li>
          </RoomItemsInfo>
        </div>
        <IconButton
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <ChevronUp width={20} height={20} color={colors.gray600} />
          ) : (
            <ChevronDown width={20} height={20} color={colors.gray600} />
          )}
        </IconButton>
      </RoomInfoHeader>
      {isExpanded && (
        <ItemsAndMaterialsContainer>
          {renderItems(items)}
          {renderMaterials(materials)}
        </ItemsAndMaterialsContainer>
      )}
    </div>
  );
};
